import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Loader2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  parseErrorMessage,
  subscribeV3BillingRequired,
  type V3BillingRequiredCode,
  type V3BillingRequiredInfo,
} from "@/lib/queryClient";
import { currentAppReturnPath, startV3CardSetup, startV3Plan, useV3Billing, v3StartLabel } from "@/hooks/use-v3-billing";

/** After a dismissal, ignore the same code for this long (background writes can repeat). */
const REPROMPT_COOLDOWN_MS = 30_000;

/** Background query 402s prompt at most once per code per page load. */
const promptedFromQuery = new Set<V3BillingRequiredCode>();

/** Test hook. */
export function resetV3BillingRequiredListenerForTest(): void {
  promptedFromQuery.clear();
}

const DEFAULT_MESSAGES: Record<V3BillingRequiredCode, string> = {
  ACCOUNT_READ_ONLY: "Your account is read-only. Add a card to pick up where you left off.",
  HOST_TRIAL_REQUIRED: "Start your 14-day Host trial to use this feature.",
  RESEARCH_CAP: "You've used your free searches this month. Subscribe for unlimited research.",
};

/**
 * App-wide handler for v3 billing 402s:
 *   ACCOUNT_READ_ONLY                  → message + "Add a card" (Stripe-hosted card setup)
 *   HOST_TRIAL_REQUIRED / RESEARCH_CAP → message + "Start your 14-day Host trial", or
 *                                        "Subscribe — $49/month" once the trial is used
 *                                        (hitting the research cap uses it — D36)
 */
export function V3BillingRequiredListener() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const v3Status = useV3Billing();
  const [prompt, setPrompt] = useState<{ code: V3BillingRequiredCode; message: string } | null>(null);
  const openRef = useRef(false);
  openRef.current = prompt !== null;
  const dismissedAtRef = useRef<Partial<Record<V3BillingRequiredCode, number>>>({});

  useEffect(() => {
    return subscribeV3BillingRequired((info: V3BillingRequiredInfo) => {
      if (openRef.current) return;
      const dismissedAt = dismissedAtRef.current[info.code];
      if (dismissedAt && Date.now() - dismissedAt < REPROMPT_COOLDOWN_MS) return;
      if (info.source === "query") {
        if (promptedFromQuery.has(info.code)) return;
        promptedFromQuery.add(info.code);
      }
      setPrompt({ code: info.code, message: info.message || DEFAULT_MESSAGES[info.code] });
    });
  }, []);

  const actionMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (code: V3BillingRequiredCode) => {
      if (code === "ACCOUNT_READ_ONLY") {
        await startV3CardSetup(currentAppReturnPath());
        return { redirected: true };
      }
      const data = await startV3Plan();
      return { redirected: !!data.url };
    },
    onSuccess: (result) => {
      if (result.redirected) return;
      setPrompt(null);
      toast({ title: "Your 14-day Host trial has started", description: "Every Host feature is unlocked." });
      // Everything gated on the old state (status, entitlements, pages) needs a fresh read.
      queryClient.invalidateQueries();
    },
    onError: (err) => {
      toast({
        title: "Something went wrong",
        description: parseErrorMessage(err, "We couldn't update your plan just now. Please try again."),
        variant: "destructive",
      });
    },
  });

  const close = () => {
    if (actionMutation.isPending) return;
    if (prompt) dismissedAtRef.current[prompt.code] = Date.now();
    setPrompt(null);
  };

  const isReadOnly = prompt?.code === "ACCOUNT_READ_ONLY";

  return (
    <Dialog
      open={prompt !== null}
      onOpenChange={(next) => {
        if (actionMutation.isPending) return;
        if (!next) close();
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        data-testid="dialog-v3-billing-required"
        onInteractOutside={(e) => {
          if (actionMutation.isPending) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (actionMutation.isPending) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (actionMutation.isPending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{isReadOnly ? "Your account is read-only" : "Unlock everything with a Host trial"}</DialogTitle>
          <DialogDescription data-testid="text-v3-billing-required-message">{prompt?.message}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={close} disabled={actionMutation.isPending} data-testid="button-v3-billing-required-later">
            Not now
          </Button>
          <Button
            className="bg-brand hover:bg-brand-hover text-white"
            disabled={actionMutation.isPending || !prompt}
            onClick={() => prompt && actionMutation.mutate(prompt.code)}
            data-testid={isReadOnly ? "button-v3-add-card" : "button-v3-start-trial"}
          >
            {actionMutation.isPending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : isReadOnly ? (
              <CreditCard className="mr-1.5 h-4 w-4" />
            ) : (
              <Sparkles className="mr-1.5 h-4 w-4" />
            )}
            {isReadOnly
              ? "Add a card"
              : prompt?.code === "RESEARCH_CAP"
                ? v3StartLabel(v3Status ? { ...v3Status, trialAvailable: false } : { trialAvailable: false })
                : v3StartLabel(v3Status)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
